import { REPORT_TOOL, verificationIssues, type AgentEvent } from "./api";

export type ProgressTone = "info" | "success" | "warn" | "error";

export interface ProgressEntry {
  label: string;
  tone: ProgressTone;
}

const MEMORY_SKILL = "_learned_memory";

const TOOL_LABELS: Record<string, string> = {
  convert_contract: "Converting the contract to text",
  read_contract: "Reading the contract",
  [REPORT_TOOL]: "Checking terms against the contract, fetching Admin API usage and pricing overages"
};

function toolErrorText(output: Record<string, unknown> | undefined): string {
  const detail = output?.error ?? output?.message ?? output?.content;
  return typeof detail === "string" ? detail : JSON.stringify(output ?? {});
}

export function progressEntry(event: AgentEvent): ProgressEntry | null {
  switch (event.type) {
    case "input_message":
      return null;
    case "artifact_upload":
      return {
        label: `Uploaded ${event.artifacts?.map((artifact) => artifact.filename).join(", ") ?? "contract"}`,
        tone: "success"
      };
    case "skill_load":
      if (event.skill_name !== MEMORY_SKILL) return { label: `Loaded skill ${event.skill_name}`, tone: "info" };
      return typeof event.content === "string" && event.content.trim() !== ""
        ? { label: "Recalled lessons learned from earlier reports", tone: "success" }
        : { label: "Checked memory: nothing learned yet", tone: "info" };
    case "tool_input": {
      const name = event.tool_configuration_name ?? "";
      if (name === REPORT_TOOL) {
        const terms = Array.isArray(event.tool_input?.terms) ? event.tool_input.terms.length : 0;
        const customers = Array.isArray(event.tool_input?.customer_ids) ? event.tool_input.customer_ids.length : 0;
        return { label: `${TOOL_LABELS[REPORT_TOOL]} (${terms} contract terms, ${customers} customers)`, tone: "info" };
      }
      return TOOL_LABELS[name] ? { label: TOOL_LABELS[name], tone: "info" } : null;
    }
    case "tool_output":
      if (event.error === true) {
        if (event.tool_configuration_name === REPORT_TOOL) {
          const issues = verificationIssues(event.tool_output);
          if (issues.length === 0) {
            return { label: `Pricing failed: ${toolErrorText(event.tool_output).split("\n")[0]}`, tone: "error" };
          }
          return {
            label: `Self-check rejected the terms (${issues.length} issue${issues.length === 1 ? "" : "s"}): ${
              issues[0]
            } - correcting`,
            tone: "warn"
          };
        }
        return { label: `${event.tool_configuration_name} failed: ${toolErrorText(event.tool_output)}`, tone: "error" };
      }
      return event.tool_configuration_name === REPORT_TOOL
        ? { label: "Terms verified against the contract; overages priced", tone: "success" }
        : null;
    case "structured_output":
      return { label: "Report ready", tone: "success" };
    case "error":
      return {
        label: (event.messages ?? []).join("; ") || "The agent stopped with an unexpected error",
        tone: "error"
      };
    default:
      return null;
  }
}
